/**
 * Audit Log Component
 *
 * Display configuration change history with filters. Layout mirrors
 * the WorkflowVersions timeline: GitHub-style date headers (Today /
 * Yesterday / long-date) sitting on a vertical rail hairline, with
 * one compact card per audit entry.
 *
 * Data fetching is unchanged — the existing auditLog array from
 * ConfigurationContext flows straight into the date-grouping helper.
 */

import React, { useState } from 'react';
import { useConfiguration } from '../../../contexts/ConfigurationContext';
import { Badge } from '../../../components/ui/Badge';
import { Input } from '../../../components/ui/Input';
import { ConfigListSkeleton } from './ConfigurationSkeleton';
import { formatRelativeTime } from '../../../utils/formatRelativeTime';
import { groupAuditEntriesByDate } from '../utils/groupByDate';
import styles from './AuditLog.module.css';

const CHANGE_TYPES = {
  update: 'Update',
  activate: 'Activate',
  delete: 'Delete'
};

// Map each change_type → a 2.0 <Badge> palette colour (rendered as a
// surface pill): update→blue, activate→green, delete→red.
const CHANGE_TYPE_COLOR = {
  update: 'blue',
  activate: 'green',
  delete: 'red'
};

const initials = (name) => {
  if (!name || name === 'system') return 'S';
  return name
    .split(/\s+/)
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
};

export default function AuditLog() {
  const { auditLog, loading } = useConfiguration();
  const [filterType, setFilterType] = useState('ALL');
  const [searchUser, setSearchUser] = useState('');

  const filteredLogs = auditLog.filter((log) => {
    const typeMatch = filterType === 'ALL' || log.change_type === filterType;
    const userMatch = !searchUser || log.changed_by.toLowerCase().includes(searchUser.toLowerCase());
    return typeMatch && userMatch;
  });

  if (loading && auditLog.length === 0) {
    return <ConfigListSkeleton />;
  }

  const groups = groupAuditEntriesByDate(filteredLogs);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2>Configuration Audit Log</h2>
      </div>

      {/* Filters */}
      <div className={styles.filtersContainer}>
        <div className={styles.filterGroup}>
          <label>Filter by Type</label>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className={styles.select}
          >
            <option value="ALL">All Changes</option>
            {Object.entries(CHANGE_TYPES).map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </select>
        </div>

        <div className={styles.filterGroup}>
          <label>Search by User</label>
          <Input
            type="text"
            placeholder="Enter username or email..."
            value={searchUser}
            onChange={(e) => setSearchUser(e.target.value)}
            className={styles.input}
          />
        </div>

        <div className={styles.filterStats}>
          <span className={styles.statLabel}>Showing</span>
          <span className={styles.statValue}>{filteredLogs.length}</span>
          <span className={styles.statLabel}>of {auditLog.length} changes</span>
        </div>
      </div>

      {/* Audit Log Timeline */}
      {filteredLogs.length === 0 ? (
        <div className={styles.noData}>
          <h3>No Audit Logs Found</h3>
          <p>
            {searchUser || filterType !== 'ALL'
              ? 'No changes match your filters. Try adjusting your search criteria.'
              : 'No configuration changes have been logged yet.'}
          </p>
        </div>
      ) : (
        <div className={styles.rail}>
          {groups.map((group) => (
            <React.Fragment key={group.isoDate}>
              <div className={styles.dateRow}>
                <span className={styles.dateNode} aria-hidden="true" />
                <span className={styles.dateLabel}>
                  Activity on {group.label}
                </span>
              </div>

              {group.entries.map((log, index) => {
                const typeLabel = CHANGE_TYPES[log.change_type] || log.change_type;
                const hasBody = Boolean(log.reason || log.description || log.changes_summary);

                return (
                  <div key={`${log.changed_at}-${index}`} className={styles.entry}>
                    <div className={styles.card}>
                      <div className={styles.cardHeader}>
                        <span className={styles.versionId}>
                          v{log.version || '–'}
                        </span>

                        <span className={styles.summaryLine}>
                          <Badge variant="soft" radius="full" color={CHANGE_TYPE_COLOR[log.change_type]}>
                            {typeLabel}
                          </Badge>
                          <span className={styles.summary}>
                            {log.description || log.reason || `Configuration ${typeLabel.toLowerCase()}`}
                          </span>
                        </span>

                        <span className={styles.metaCol}>
                          <span className={styles.actor}>
                            <span className={styles.avatar}>
                              {initials(log.changed_by)}
                            </span>
                            {log.changed_by}
                          </span>
                          <span className={styles.timestamp}>
                            {formatRelativeTime(log.changed_at)}
                          </span>
                        </span>
                      </div>

                      {hasBody && (
                        <div className={styles.detail}>
                          {log.reason && (
                            <div className={styles.detailSection}>
                              <h4>Reason</h4>
                              <p>{log.reason}</p>
                            </div>
                          )}
                          {log.description && (
                            <div className={styles.detailSection}>
                              <h4>Description</h4>
                              <p>{log.description}</p>
                            </div>
                          )}
                          {log.changes_summary && (
                            <div className={styles.detailSection}>
                              <h4>Changes</h4>
                              <ul className={styles.changesList}>
                                {Object.entries(log.changes_summary).map(([key, value]) => (
                                  <li key={key}>
                                    <span className={styles.changeKey}>{key}:</span> {value}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
}
